import { useRef, useLayoutEffect, useEffect, useState } from 'react';
import { useDrag } from '@use-gesture/react';

const DRAG_THRESHOLD = 8;       // px — dead zone before track starts following finger
const SNAP_THRESHOLD = 50;      // px — minimum finger travel to commit a tab change
const VELOCITY_THRESHOLD = 0.5; // px/ms — fast flick commits even below SNAP_THRESHOLD
// Duration scales with travel distance (fraction of one panel width).
const MIN_DURATION_MS = 80;
const MAX_DURATION_MS = 360;
// Soft ease-in-out: gentle start and end, smooth feel for both snap and commit.
const EASING = 'cubic-bezier(0.4, 0, 0.2, 1)';

/**
 * Full-screen swipe-to-change-tab hook.
 *
 * Attaches gesture listeners directly to `window` so the user can swipe from
 * anywhere on the screen — including empty areas and the bottom nav strip.
 *
 * Pass `enabled={currentPage === '<page>'}` to ensure only the currently
 * visible page responds when multiple pages are kept mounted by WarbandLayout.
 *
 * Returns { containerRef, trackRef } to wire up <SwipeTabs>.
 * No bind() spreading required on any element.
 */
export function useSwipeTabs(
  tabIndex: number,
  numTabs: number,
  onChange: (index: number) => void,
  enabled: boolean = true,
) {
  const containerRef = useRef<HTMLDivElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);

  // Cached at drag-start (first: true) to avoid per-frame layout reads.
  const containerWidthRef = useRef(0);

  // Tracks the last written translateX percentage so buildTransition can
  // compute the travel distance for proportional duration.
  const currentPctRef = useRef(0);

  // When true the drag handler has already animated the track into its final
  // position — useLayoutEffect should skip its own animation for that render.
  const isSwipingRef = useRef(false);

  // Stable ref to window so @use-gesture/react can attach native listeners.
  const windowRef = useRef(window);

  // MUI sets aria-hidden="true" on #root whenever any Modal/Dialog is open.
  // Track that here so we can fully detach the gesture and not interfere with
  // interactions inside dialogs (local state dialogs as well as store-driven ones).
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    const root = document.getElementById('root');
    if (!root) return;

    const sync = () => setIsModalOpen(root.getAttribute('aria-hidden') === 'true');
    sync(); // read initial state

    const observer = new MutationObserver(sync);
    observer.observe(root, { attributes: true, attributeFilter: ['aria-hidden'] });
    return () => observer.disconnect();
  }, []);

  // ── Helpers ────────────────────────────────────────────────────────────────

  function buildTransition(toPct: number): string {
    // Express travel as a fraction of one panel width (0 = no travel, 1 = full panel).
    const panelPct = 100 / numTabs;
    const ratio = Math.min(Math.abs(toPct - currentPctRef.current) / panelPct, 1);
    const duration = Math.round(MIN_DURATION_MS + ratio * (MAX_DURATION_MS - MIN_DURATION_MS));
    return `transform ${duration}ms ${EASING}`;
  }

  function setTrackPosition(px: number, animated: boolean) {
    const el = trackRef.current;
    if (!el) return;
    const cw = containerWidthRef.current || window.innerWidth;
    // Use percentage (relative to the track's own width) instead of pixels so
    // the position stays in the same coordinate space as the CSS percentage-
    // sized flex panels. This avoids sub-pixel gaps caused by offsetWidth
    // integer rounding diverging from the browser's fractional layout values.
    const pct = (px / (numTabs * cw)) * 100;
    if (animated) {
      el.style.transition = buildTransition(pct);
      // Force the browser to flush and commit the current computed style before
      // we write the new transform. Without this, when the previous frame set
      // `transition: none`, the browser may batch the transition + transform
      // writes together and skip the animation entirely.
      // eslint-disable-next-line @typescript-eslint/no-unused-expressions
      void el.offsetHeight;
    } else {
      el.style.transition = 'none';
    }
    currentPctRef.current = pct;
    el.style.transform = `translateX(${pct}%)`;
  }

  // ── Sync track when tab changes externally (e.g. BottomNavigation tap) ─────

  useLayoutEffect(() => {
    // Refresh container width in case the window was resized.
    if (containerRef.current) {
      containerWidthRef.current = containerRef.current.offsetWidth;
    }

    if (isSwipingRef.current) {
      // Drag already animated the track into position — just lock it in place.
      isSwipingRef.current = false;
      setTrackPosition(-(tabIndex * containerWidthRef.current), false);
      return;
    }

    setTrackPosition(-(tabIndex * containerWidthRef.current), true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabIndex]);

  // ── Gesture ────────────────────────────────────────────────────────────────

  useDrag(
    ({ first, down, movement: [mx], velocity: [vx] }) => {
      if (first) {
        containerWidthRef.current =
          containerRef.current?.offsetWidth ?? window.innerWidth;
      }

      const cw = containerWidthRef.current;
      const baseOffset = -(tabIndex * cw);

      if (down) {
        // Apply dead zone: track only follows once finger has moved DRAG_THRESHOLD px.
        // Raw mx is still used for the commit threshold check below.
        const effective = Math.sign(mx) * Math.max(0, Math.abs(mx) - DRAG_THRESHOLD);
        const raw = baseOffset + effective;
        const min = -((numTabs - 1) * cw);
        const clamped = Math.max(min, Math.min(0, raw));
        setTrackPosition(clamped, false);
      } else {
        // Drag released — commit or snap back.
        const shouldCommit =
          Math.abs(mx) > SNAP_THRESHOLD || Math.abs(vx) > VELOCITY_THRESHOLD;

        if (shouldCommit && mx !== 0) {
          const newIndex =
            mx < 0
              ? Math.min(tabIndex + 1, numTabs - 1)
              : Math.max(tabIndex - 1, 0);

          if (newIndex !== tabIndex) {
            isSwipingRef.current = true;
            setTrackPosition(-(newIndex * cw), true);
            onChange(newIndex);
            return;
          }
        }

        // Not enough movement, or already at a boundary — snap back.
        setTrackPosition(baseOffset, true);
      }
    },
    {
      target: windowRef,
      axis: 'x',
      filterTaps: true,
      enabled: enabled && !isModalOpen,
    },
  );

  return { containerRef, trackRef };
}
